import { forwardRef, Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { cn } from 'lib/utils';
import { Icon } from 'components/ui/icon';
import { NavItem } from './nav-item';
import { NavItemLabel } from './nav-item-label';
import {
  NAV_ITEM_BASE_CLASSES,
  TRANSITION_DURATION,
  TRANSITION_EASING,
} from './nav-item-constants';
import {
  hasActiveChild,
  checkNavItemAuthorization,
  getCollapsibleActiveClasses,
  getCollapsedClasses,
  getCollapsibleOpenClasses,
} from './utils';
import { useEntityPermissions } from './useSidebar';
import type { NavItemCollapsibleProps } from './types';
import { Button } from 'components/ui/button';

const NavItemCollapsible = forwardRef<HTMLDivElement, NavItemCollapsibleProps>(
  (
    { item, onNavigate, isCollapsed = false, defaultOpen = false, className },
    ref
  ) => {
    const location = useLocation();
    const { orgId } = useParams();
    const entityPermissions = useEntityPermissions();

    const visibleChildren = useMemo(
      () =>
        item.children?.filter((child) =>
          checkNavItemAuthorization(child, entityPermissions)
        ) ?? [],
      [item.children, entityPermissions]
    );

    const hasActive = hasActiveChild(item, location.pathname, orgId || '');
    const [isOpen, setIsOpen] = useState(defaultOpen || hasActive);

    useEffect(() => {
      if (hasActive) setIsOpen(true);
    }, [hasActive]);

    if (visibleChildren.length === 0) return null;

    const showChildren = isOpen;
    const contentId = `nav-collapsible-${item.id}`;

    return (
      <div
        ref={ref}
        className={cn(
          className,
          isCollapsed && isOpen && 'bg-sidebar-hover/70 rounded-lg my-1'
        )}
      >
        <Button
          variant={'ghost'}
          rounded="lg"
          onClick={() => {
            if (isOpen && hasActive) return;
            setIsOpen((prev) => !prev);
          }}
          className={cn(
            NAV_ITEM_BASE_CLASSES,
            'w-full flex items-center gap-0 rounded-b-none',
            isCollapsed ? 'justify-center' : 'justify-start',
            getCollapsedClasses(isCollapsed, false),
            getCollapsibleActiveClasses(hasActive),
            getCollapsibleOpenClasses(isCollapsed, isOpen)
          )}
          iconStart={item.icon}
          iconSize={20}
          aria-expanded={showChildren}
          aria-controls={contentId}
          title={isCollapsed ? item.name : undefined}
        >
          {!isCollapsed && (
            <Fragment>
              <NavItemLabel isCollapsed={isCollapsed}>{item.name}</NavItemLabel>
              <Icon
                name={isOpen ? 'chevron-up' : 'chevron-down'}
                size={16}
                className="shrink-0 text-gray-blue-50 ml-auto"
                aria-hidden="true"
              />
            </Fragment>
          )}
        </Button>

        <div
          id={contentId}
          role="region"
          className={cn(
            `grid transition-all ${TRANSITION_DURATION} ${TRANSITION_EASING}`,
            showChildren
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          )}
        >
          <ul className={cn('overflow-hidden m-0 p-0 list-none')}>
            {visibleChildren.map((child) => (
              <li key={child.id}>
                <NavItem
                  item={child}
                  onNavigate={onNavigate}
                  isCollapsed={isCollapsed}
                  isNested
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }
);

NavItemCollapsible.displayName = 'NavItemCollapsible';

export { NavItemCollapsible };
